import { AppLogger, DatadogClient } from '@workivate/tho-web-shared';
import PackageInfo from '../../package.json';
const { version: appVersion } = PackageInfo;

interface KeyValue {
  [key: string]:
    | string
    | string[]
    | string[][]
    | boolean
    | number
    | undefined
    | {};
}

const isKeyValue = (body: any): body is KeyValue => {
  return body !== null && typeof body === 'object' && !Array.isArray(body);
};

export interface Options {
  urlParameters?: KeyValue;
  headers?: RequestInit['headers'];
  version?: number;
  body?: RequestInit['body'] | KeyValue;
  method?: string;
  shouldPersistLoginPageLanguage?: boolean;
}

const ACCEPT_VERSIONS: { [key: number]: string } = {
  1.3: 'application/vnd.wam-api-v1.3+json',
  1.4: 'application/vnd.wam-api-v1.4+json',
  1.5: 'application/vnd.wam-api-v1.5+json',
};

const myFetch = async <T>(
  url: string,
  options: Options | undefined = {}
): Promise<T> => {
  // Custom Header with app information to append
  const customUserAgentInfo = `app:usl env:${
    import.meta.env.DEPLOYMENT_ENVIRONMENT
  } version:${appVersion}`;

  const params: RequestInit = {
    method: options.method,
    headers: {
      ...(options.headers as Record<string, string>),
      Accept: ACCEPT_VERSIONS[options.version ?? 0] ?? 'application/json',
      'Content-Type': 'application/json',
      'X-Client-App-Info': customUserAgentInfo,
      // This header facilitates Datadog tracing UI-4445
      // Some BE endpoints may not handle this header and additional logic to strip it may be required in the future
      ...DatadogClient.getCorrelationIdHeader(),
    },
  };

  const { urlParameters } = options;
  const client_id = isKeyValue(options.body) && options.body.client_id;
  // lang parameter is used by BE to persist the user's language selection across login, especially SSO
  // if the user has not made any selection or we are in a native app, we do not send lang
  // the originating app will handle the language selection
  // so if shouldPersistLoginPageLanguage is false, we remove lang from body so as not to override the web app setting
  // if is_native is true, we also remove lang as the native app handles language selection
  if (isKeyValue(options.body)) {
    if (!options.shouldPersistLoginPageLanguage || options.body.is_native) {
      delete options.body.lang;
    }

    // is_native is used by USL FE and should not be sent to the BE
    if (options.body.is_native) {
      delete options.body.is_native;
    }
    params.body = JSON.stringify(options.body);
  }

  if (urlParameters) {
    const urlObj = new URL(url); // URL methods encode space as '+' and iOS doesn't handle this. Here we are sending to our own BE and we are not sending tenancy_hash which is the only param we expect to contain spaces, so it should be ok, but it's important to be aware when using the URL object.
    const params = new URLSearchParams(urlObj.search);

    Object.entries(urlParameters).forEach(([key, value]) => {
      // if the parameter already exists, overwrite it
      // if we append, there could be two instances of the same parameter
      params.set(key, String(value));
    });

    urlObj.search = params.toString();
    url = urlObj.toString();
  }

  const response = await fetch(url, params);
  const { status } = response;

  const json = await response.json();

  if (status >= 200 && status < 300) {
    return json;
  }

  const errorMsgFromBe = json?.errors?.[0]?.message ?? json?.error?.message;

  const requestId = json?.request_id;
  const datadogRequestIdUrl = `https://app.datadoghq.com/logs?query=@lambda.request_id:${requestId}`;
  const datadogClientIdUrl = `https://app.datadoghq.com/logs?query=@public_client_id:${client_id}`;

  AppLogger.warn('Request ID Log:', datadogRequestIdUrl);
  AppLogger.warn('Client ID Log:', datadogClientIdUrl);

  const error = new DatadogClient.CustomError(
    json?.errors ?? [],
    errorMsgFromBe || json.error?.message || json.error,
    json?.error?.code,
    status
  );

  // Log the error to Datadog
  DatadogClient.addError(error, {
    httpStatus: status,
    responseBody: error,
    requestUrl: url,
    requestParams: params,
  });

  DatadogClient.logMessage(`FE USL Error: ${errorMsgFromBe}`);

  throw error;
};

export default myFetch;
